import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CalculationResult } from '@/types/solidarity';
import { Award, RefreshCw } from 'lucide-react';

interface ResultDisplayProps {
  result: CalculationResult;
}

const FIXED_DISCLAIMER_TEXT = "Toto hodnocení berte s rezervou. Je to spíše taková hra. Index vznikl pro zachycení celkové míry solidarity ve společnosti, ale nedokáže přesně reflektovat osobní situaci jedince. Ne všichni se nacházíme v situaci nebo životní fázi, kdy máme kapacitu pomáhat. Někdy naopak spíše pomoc potřebujeme a to je v pořádku. I na to má index solidarity odkazovat.";

export const ResultDisplay: React.FC<ResultDisplayProps> = ({ result }) => {
  const handleRestart = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-3xl mx-auto pt-4 sm:pt-8">
        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-6 px-4 sm:px-6">
            <div className="flex items-center justify-center mb-4">
              <Award className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-600 mr-2" />
              <CardTitle className="text-xl sm:text-2xl font-bold text-slate-800">
                Váš Index Solidarity
              </CardTitle>
            </div>
            <div className="text-center space-y-2">
              <div className="text-4xl sm:text-6xl font-bold text-indigo-600">
                {result.index_rescaled}
              </div>
              {result.index_rescaled >= 20 && (
                <Badge variant="secondary" className="text-sm sm:text-lg px-3 py-1 sm:px-4 sm:py-2">
                  {result.percentile}. percentil
                </Badge>
              )}
            </div>
          </CardHeader>

          <CardContent className="space-y-6 px-4 sm:px-6">
            {/* Result explanation */}
            <div className="bg-blue-50 rounded-lg p-4 sm:p-6">
              <p className="text-slate-700 leading-relaxed text-sm sm:text-base">
                {result.custom_text}
              </p>
            </div>

            {/* Sub-indexes breakdown */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 border">
                <h3 className="font-semibold text-slate-700 mb-2 text-sm sm:text-base">Dobrovolnictví</h3>
                <div className="text-xl sm:text-2xl font-bold text-green-600">
                  {Math.round(result.index_volunteer)}/5
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <h3 className="font-semibold text-slate-700 mb-2 text-sm sm:text-base">Dárcovství</h3>
                <div className="text-xl sm:text-2xl font-bold text-blue-600">
                  {Math.round(result.index_charity)}/5
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <h3 className="font-semibold text-slate-700 mb-2 text-sm sm:text-base">Postoje</h3>
                <div className="text-xl sm:text-2xl font-bold text-purple-600">
                  {Math.round(result.index_attitudes)}/5
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-6">
              <h3 className="font-semibold text-amber-800 mb-3 text-sm sm:text-base">Důležité upozornění</h3>
              <p className="text-amber-700 leading-relaxed text-sm">
                {FIXED_DISCLAIMER_TEXT}
              </p>
            </div>

            {/* Restart button */}
            <div className="text-center pt-4">
              <Button
                onClick={handleRestart}
                className="w-full sm:w-auto px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Vyplnit znovu
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};